import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";
import { replaceNotificationVariables } from "@/lib/notificationVariables";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { ExternalLink } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";
interface NotificationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification: {
    id: string;
    title_it: string;
    title_en: string | null;
    title_es: string | null;
    description_it: string | null;
    description_en: string | null;
    description_es: string | null;
    image_url: string | null;
    cta_url: string | null;
    cta_text_it: string | null;
    cta_text_en: string | null;
    cta_text_es: string | null;
    created_at: string;
  } | null;
}

export default function NotificationDetailModal({
  isOpen,
  onClose,
  notification,
}: NotificationDetailModalProps) {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  if (!notification) return null;

  const userVariables = {
    firstName: user?.user_metadata?.first_name,
    lastName: user?.user_metadata?.last_name,
    email: user?.email,
  };

  const getTitle = () => {
    switch (i18n.language) {
      case "en":
        return notification.title_en || notification.title_it;
      case "es":
        return notification.title_es || notification.title_it;
      default:
        return notification.title_it;
    }
  };

  const getDescription = () => {
    let html: string | null;
    switch (i18n.language) {
      case "en":
        html = notification.description_en || notification.description_it;
        break;
      case "es":
        html = notification.description_es || notification.description_it;
        break;
      default:
        html = notification.description_it;
    }
    const processedHtml = replaceNotificationVariables(html, userVariables);
    // Sanitize HTML to prevent XSS attacks
    return processedHtml ? sanitizeHtml(processedHtml, {
      allowedTags: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'a', 'br', 'blockquote'],
      allowedAttributes: ['href', 'target', 'rel', 'title']
    }) : null;
  };

  const getCtaText = () => {
    if (!notification.cta_url) return null;
    switch (i18n.language) {
      case "en":
        return notification.cta_text_en || notification.cta_text_it || "Learn more";
      case "es":
        return notification.cta_text_es || notification.cta_text_it || "Saber más";
      default:
        return notification.cta_text_it || "Scopri di più";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        {/* Image */}
        {notification.image_url && (
          <div className="aspect-video w-full">
            <img
              src={notification.image_url}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {getTitle()}
            </DialogTitle>
          </DialogHeader>

          {/* Description */}
          {getDescription() && (
          <ScrollArea className="mt-4 max-h-[400px]">
              <div
                className="prose prose-sm max-w-none 
                           prose-headings:font-heading prose-headings:font-bold prose-headings:text-foreground
                           prose-p:text-foreground prose-p:leading-relaxed
                           prose-a:text-primary prose-a:font-bold prose-a:no-underline hover:prose-a:underline
                           prose-strong:font-bold prose-strong:text-foreground
                           prose-ul:list-disc prose-ol:list-decimal
                           prose-li:text-foreground"
                dangerouslySetInnerHTML={{ __html: getDescription() }}
              />
            </ScrollArea>
          )}

          {/* CTA Button */}
          {notification.cta_url && (
            <div className="mt-6 pt-4 border-t border-border">
              <a
                href={notification.cta_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <BrutalButton variant="primary" className="w-full">
                  {getCtaText()}
                  <ExternalLink className="h-4 w-4 ml-2" />
                </BrutalButton>
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
